import React, { useEffect, useState } from "react";
import { useParams, useNavigate } from "react-router-dom";
import { toast } from "react-toastify";
import { usePageLoader } from "../../_helper/loading/LoadingContext";
import { viewStaffApi } from "../../Api/HRStaffApi";
import { viewSalaryStructureApi } from "../../Api/salaryApi";
import { getLetterConfigApi } from "../../Api/letterSettingsApi";
import { checkStatusCodeSuccess } from "../../Constant/common";
import BaseButton from "../../BaseComponents/BaseButton";
import BackButton from "../../BaseComponents/BackButton";
import BaseModal from "../../BaseComponents/BaseModal";
import {
  FileText,
  Download,
  Printer,
  Briefcase,
  UserCheck,
  BookOpen,
  Award,
  FileMinus,
} from "react-feather";
import logoLight from "../../assets/images/logo/white-based-logo-long.svg";
import {
  DEFAULT_OFFER_TEMPLATE,
  DEFAULT_JOINING_TEMPLATE,
  DEFAULT_TRAINING_TEMPLATE,
  DEFAULT_EXPERIENCE_TEMPLATE,
  DEFAULT_RELIEVING_TEMPLATE,
  getImageUrl,
  resolvePlaceholders,
  downloadLetterPdf,
  printLetter,
  formatBodyToHtml,
} from "../../utils/letterUtils";
import { logError } from "../../utils/logger";

const LetterPreview = ({
  staff,
  bodyText,
  includeSignature,
  signatory,
  signaturePath,
  signatoryTitle,
  includeLogo,
  logoPath,
  logoWidth,
  logoAlign,
  logoBase64,
  signatureBase64,
}) => {
  const orgName = staff?.organization?.organization_name || "our organization";
  const centerName = staff?.primaryCenter?.center_name || "office";

  return (
    <div
      style={{
        background: "#fff",
        color: "#333",
        padding: "30px",
        borderRadius: "12px",
        fontFamily: "'Inter', sans-serif",
        fontSize: "0.875rem",
        lineHeight: "1.6",
        maxWidth: "100%",
        margin: "0 auto",
        border: "1px solid #eaeaea",
        textAlign: "left",
      }}
    >
      {/* Letterhead */}
      <div
        style={{
          borderBottom: "2px solid #33",
          paddingBottom: "12px",
          marginBottom: "20px",
          display: "flex",
          flexDirection: includeLogo
            ? logoAlign === "center"
              ? "column"
              : logoAlign === "left"
                ? "row"
                : "row-reverse"
            : "row",
          justifyContent: "space-between",
          alignItems: "center",
          gap: "12px",
        }}
      >
        {/* Logo */}
        {includeLogo && (
          <div>
            <img
              src={
                logoBase64
                  ? logoBase64
                  : logoPath && logoPath !== "logo.png" && logoPath !== "/logo.png"
                    ? getImageUrl(logoPath)
                    : logoLight
              }
              alt="Logo"
              style={{
                width: `${logoWidth}px`,
                height: "auto",
                display: "block",
              }}
            />
          </div>
        )}

        {/* Organization Details */}
        <div
          style={{
            textAlign: includeLogo
              ? logoAlign === "center"
                ? "center"
                : logoAlign === "left"
                  ? "right"
                  : "left"
              : "left",
          }}
        >
          <h2
            style={{
              margin: 0,
              fontWeight: 800,
              color: "#111",
              fontSize: "1.2rem",
              letterSpacing: "-0.02em",
            }}
          >
            {orgName.toUpperCase()}
          </h2>
          <p style={{ margin: "2px 0 0 0", color: "#666", fontSize: "0.75rem", fontWeight: 500 }}>
            {centerName}
          </p>
        </div>

        {/* Render Official Document badge only when NOT showing logo */}
        {!includeLogo && (
          <div
            style={{
              padding: "4px 10px",
              border: "2px solid #333",
              fontSize: "0.75rem",
              fontWeight: 800,
              textTransform: "uppercase",
              letterSpacing: "0.05em",
            }}
          >
            Official Document
          </div>
        )}
      </div>

      {/* Body */}
      <div
        className="letter-preview-content"
        style={{ minHeight: "180px", color: "#222" }}
        dangerouslySetInnerHTML={{ __html: formatBodyToHtml(bodyText) }}
      />

      {/* Signature Section */}
      <div
        style={{
          marginTop: "30px",
          display: "flex",
          justifyContent: "space-between",
          alignItems: "flex-end",
        }}
      >
        <div>
          <p style={{ margin: 0, fontSize: "0.75rem", color: "#666" }}>Prepared By</p>
          <p style={{ margin: "2px 0 0 0", fontWeight: 600, color: "#222", fontSize: "0.8125rem" }}>
            HR Fusions
          </p>
        </div>
        <div style={{ position: "relative", textAlign: "right" }}>
          {includeSignature && (
            <>
              {/* Digital stamp badge */}
              <div
                style={{
                  position: "absolute",
                  right: "50px",
                  bottom: "15px",
                  width: "60px",
                  height: "60px",
                  borderRadius: "50%",
                  border: "2px dashed rgba(99,102,241,0.3)",
                  display: "flex",
                  alignItems: "center",
                  justifyContent: "center",
                  fontSize: "0.45rem",
                  color: "rgba(99,102,241,0.5)",
                  fontWeight: 800,
                  textTransform: "uppercase",
                  transform: "rotate(-15deg)",
                  pointerEvents: "none",
                }}
              >
                Approved
              </div>
              {/* Signature Image or Stylized Name */}
              {signatureBase64 ? (
                <img
                  src={signatureBase64}
                  crossOrigin="anonymous"
                  alt="Signature"
                  style={{
                    maxHeight: "36px",
                    display: "block",
                    marginLeft: "auto",
                    marginBottom: "4px",
                  }}
                />
              ) : signaturePath ? (
                <img
                  src={getImageUrl(signaturePath)}
                  alt="Signature"
                  style={{
                    maxHeight: "36px",
                    display: "block",
                    marginLeft: "auto",
                    marginBottom: "4px",
                  }}
                />
              ) : (
                <div
                  style={{
                    fontFamily: "'Courier New', Courier, monospace",
                    fontStyle: "italic",
                    fontSize: "1rem",
                    fontWeight: 700,
                    color: "#1e3a8a",
                    transform: "rotate(-5deg)",
                    marginBottom: "4px",
                    paddingRight: "10px",
                  }}
                >
                  {signatory}
                </div>
              )}
            </>
          )}
          <p style={{ margin: 0, fontSize: "0.75rem", color: "#666" }}>For {orgName}</p>
          <p style={{ margin: "2px 0 0 0", fontWeight: 700, color: "#222", fontSize: "0.8125rem" }}>
            {signatoryTitle || "Authorized Signatory"}
          </p>
          {includeSignature && (
            <span style={{ fontSize: "0.7rem", color: "#666" }}>({signatory})</span>
          )}
        </div>
      </div>
    </div>
  );
};

const DocumentLetters = () => {
  const { staffId } = useParams();
  const navigate = useNavigate();
  const { setLoading } = usePageLoader();
  const [staffData, setStaffData] = useState(null);
  const [salaryData, setSalaryData] = useState(null);

  const [isPageLoading, setIsPageLoading] = useState(true);
  const [templates, setTemplates] = useState({
    offer: DEFAULT_OFFER_TEMPLATE,
    joining: DEFAULT_JOINING_TEMPLATE,
    training: DEFAULT_TRAINING_TEMPLATE,
    experience: DEFAULT_EXPERIENCE_TEMPLATE,
    relieving: DEFAULT_RELIEVING_TEMPLATE,
  });

  const [includeLogo, setIncludeLogo] = useState(true);
  const [logoPath, setLogoPath] = useState("");
  const [logoWidth, setLogoWidth] = useState("120");
  const [logoAlign, setLogoAlign] = useState("left");
  const [logoBase64] = useState("");
  const [signatureBase64] = useState("");

  const [includeSignature, setIncludeSignature] = useState(false);
  const [signatoryName, setSignatoryName] = useState("Authorized Signatory");
  const [signatoryTitle, setSignatoryTitle] = useState("Authorized Signatory");
  const [signaturePath, setSignaturePath] = useState("");

  const [activePreviewTemplateKey, setActivePreviewTemplateKey] = useState(null);
  const [downloadingTemplateKey, setDownloadingTemplateKey] = useState(null);

  // Load staff data on mount
  useEffect(() => {
    if (staffId) {
      setTimeout(() => {
        setIsPageLoading(true);
      }, 0);
      setLoading(true);
      Promise.all([
        viewStaffApi(staffId).then((resp) => {
          if (checkStatusCodeSuccess(resp?.statusCode)) {
            setStaffData(resp?.data);
          } else {
            toast.error(resp?.message || "Failed to load staff details");
          }
        }),
        viewSalaryStructureApi(staffId)
          .then((resp) => {
            if (checkStatusCodeSuccess(resp?.statusCode)) {
              setSalaryData(resp?.data);
            }
          })
          .catch(() => {
            // Silent catch to handle unconfigured salary structures gracefully
          }),
        getLetterConfigApi()
          .then((resp) => {
            if (checkStatusCodeSuccess(resp?.statusCode)) {
              const { settings, templates: fetchedTemplates } = resp.data;
              if (fetchedTemplates) {
                setTemplates((prev) => ({
                  ...prev,
                  ...fetchedTemplates,
                }));
              }
              if (settings) {
                setIncludeLogo(settings.include_logo);
                setLogoPath(settings.logo_path || "");
                setLogoWidth(settings.logo_width ? String(settings.logo_width) : "120");
                setLogoAlign(settings.logo_align || "left");
                setIncludeSignature(settings.include_signatory_signature);
                setSignatoryName(settings.signatory_name || "Authorized Signatory");
                setSignatoryTitle(settings.signatory_title || "Authorized Signatory");
                setSignaturePath(settings.signature_path || "");
              }
            }
          })
          .catch((err) => {
            logError("Failed to load letter settings", err);
          }),
      ])
        .catch(() => {
          toast.error("Failed to load staff details");
        })
        .finally(() => {
          setLoading(false);
          setIsPageLoading(false);
        });
    }
  }, [staffId, setLoading]);

  // Settings are loaded dynamically from settings state variables

  // Dynamic parameters resolve directly using profile info
  const getActiveBody = (key) => {
    const template = templates[key] || "";
    const isExperienceOrRelieving = key === "experience" || key === "relieving";
    return resolvePlaceholders(
      template,
      staffData,
      {
        designation: staffData?.position?.position_name,
        letter_date: new Date(),
        relieving_date: isExperienceOrRelieving ? new Date() : undefined,
      },
      salaryData,
    );
  };

  const handlePrint = (key) => {
    if (!staffData) return;
    const activeBody = getActiveBody(key);
    const label = key.toUpperCase() + "_LETTER";
    printLetter(label, staffData, activeBody, {
      includeLogo,
      logoPath,
      logoWidth,
      logoAlign,
      includeSignature,
      signatoryName,
      signaturePath,
      signatoryTitle,
      logoBase64,
      signatureBase64,
    });
    toast.success(`${key.charAt(0).toUpperCase() + key.slice(1)} Letter print dialog opened.`);
  };

  const handleDownload = (key) => {
    if (!staffData) return;
    setDownloadingTemplateKey(key);
    const activeBody = getActiveBody(key);
    const label = key.toUpperCase() + "_LETTER";
    return downloadLetterPdf(label, staffData, activeBody, {
      includeLogo,
      logoPath,
      logoWidth,
      logoAlign,
      includeSignature,
      signatoryName,
      signaturePath,
      signatoryTitle,
      logoBase64,
      signatureBase64,
    })
      .then(() => {
        toast.success(
          `${key.charAt(0).toUpperCase() + key.slice(1)} Letter PDF downloaded successfully.`,
        );
      })
      .finally(() => {
        setDownloadingTemplateKey(null);
      });
  };

  if (isPageLoading) {
    return (
      <div className="animate-fade-in" style={{ paddingBottom: 24 }}>
        <style>{`
          @keyframes shimmer {
            0% {
              background-position: -200% 0;
            }
            100% {
              background-position: 200% 0;
            }
          }
          .skeleton-shimmer {
            background: linear-gradient(90deg, rgba(99, 102, 241, 0.03) 25%, rgba(99, 102, 241, 0.08) 50%, rgba(99, 102, 241, 0.03) 75%);
            background-size: 200% 100%;
            animation: shimmer 1.5s infinite;
          }
        `}</style>
        {/* Top Header skeleton */}
        <div
          style={{
            display: "flex",
            alignItems: "center",
            justifyContent: "space-between",
            marginBottom: 24,
            flexWrap: "wrap",
            gap: 12,
          }}
        >
          <div>
            <div className="skeleton-shimmer" style={{ width: 200, height: 24, borderRadius: 6 }} />
            <div
              className="skeleton-shimmer"
              style={{ width: 300, height: 16, borderRadius: 4, marginTop: 8 }}
            />
          </div>
          <div className="skeleton-shimmer" style={{ width: 100, height: 38, borderRadius: 8 }} />
        </div>

        <div className="row">
          {[1, 2, 3].map((idx) => (
            <div className="col-12 col-md-6 col-xxl-4 mb-4" key={idx}>
              <div
                className="card project-box"
                style={{
                  background: "var(--glass-bg)",
                  border: "1px solid var(--glass-border)",
                  borderRadius: 16,
                  height: "100%",
                  display: "flex",
                  flexDirection: "column",
                }}
              >
                {/* Skeleton Header */}
                <div
                  style={{
                    padding: "16px 20px",
                    borderBottom: "1px solid var(--glass-border)",
                    borderTopLeftRadius: 16,
                    borderTopRightRadius: 16,
                    display: "flex",
                    alignItems: "center",
                    gap: 8,
                  }}
                >
                  <div
                    className="skeleton-shimmer"
                    style={{ width: 16, height: 16, borderRadius: "50%" }}
                  />
                  <div
                    className="skeleton-shimmer"
                    style={{ width: 120, height: 16, borderRadius: 4 }}
                  />
                </div>
                {/* Skeleton Body */}
                <div
                  style={{
                    padding: 20,
                    flex: 1,
                    display: "flex",
                    flexDirection: "column",
                    gap: 16,
                  }}
                >
                  <div
                    className="skeleton-shimmer"
                    style={{ width: "100%", height: 14, borderRadius: 4 }}
                  />
                  <div
                    className="skeleton-shimmer"
                    style={{ width: "95%", height: 14, borderRadius: 4 }}
                  />
                  <div
                    className="skeleton-shimmer"
                    style={{ width: "60%", height: 14, borderRadius: 4 }}
                  />
                  {/* Skeleton Buttons */}
                  <div
                    style={{
                      borderTop: "1px solid var(--glass-border)",
                      paddingTop: 16,
                      display: "flex",
                      justifyContent: "flex-end",
                      gap: 10,
                      marginTop: "auto",
                    }}
                  >
                    <div
                      className="skeleton-shimmer"
                      style={{ width: 110, height: 36, borderRadius: 8 }}
                    />
                    <div
                      className="skeleton-shimmer"
                      style={{ width: 150, height: 36, borderRadius: 8 }}
                    />
                  </div>
                </div>
              </div>
            </div>
          ))}
        </div>
      </div>
    );
  }

  const PREDEFINED_TABS = {
    offer: {
      label: "Offer Letter",
      icon: Briefcase,
      color: "#f59e0b",
      bg: "rgba(245, 158, 11, 0.03)",
    },
    joining: {
      label: "Joining Letter",
      icon: UserCheck,
      color: "#10b981",
      bg: "rgba(16, 185, 129, 0.03)",
    },
    training: {
      label: "Training Letter",
      icon: BookOpen,
      color: "#06b6d4",
      bg: "rgba(6, 182, 212, 0.03)",
    },
    experience: {
      label: "Experience Letter",
      icon: Award,
      color: "#6366f1",
      bg: "rgba(99, 102, 241, 0.03)",
    },
    relieving: {
      label: "Relieving Letter",
      icon: FileMinus,
      color: "#f43f5e",
      bg: "rgba(244, 63, 94, 0.03)",
    },
  };

  const PREDEFINED_ORDER = ["offer", "joining", "training", "experience", "relieving"];

  const getTemplateDescription = (key, name) => {
    const employeeName = name || "this employee";
    switch (key) {
      case "offer":
        return (
          <>
            Generate official offer document for <strong>{employeeName}</strong>. All dynamic
            details (such as dates, designation, salary) will be automatically evaluated using their
            registered profile information and the corporate template.
          </>
        );
      case "joining":
        return (
          <>
            Generate official joining document for <strong>{employeeName}</strong>. All dynamic
            details (such as dates, designation, salary) will be automatically evaluated using their
            registered profile information and the corporate template.
          </>
        );
      case "training":
        return (
          <>
            Generate official training document for <strong>{employeeName}</strong>. All dynamic
            details (such as dates, designation, stipend/salary) will be automatically evaluated
            using their registered profile information and the corporate template.
          </>
        );
      case "experience":
        return (
          <>
            Generate official experience document for <strong>{employeeName}</strong>. All dynamic
            details (such as employment duration, final designation, exit info) will be
            automatically evaluated using their profile records.
          </>
        );
      case "relieving":
        return (
          <>
            Generate official relieving document for <strong>{employeeName}</strong>. All dynamic
            details (such as separation date, designation, and exit info) will be automatically
            evaluated using their profile records.
          </>
        );
      default:
        return (
          <>
            Generate custom {key.charAt(0).toUpperCase() + key.slice(1)} document for{" "}
            <strong>{employeeName}</strong>. All template variables will be resolved using their
            profile information.
          </>
        );
    }
  };

  const getButtonColor = (key) => {
    switch (key) {
      case "offer":
        return "warning";
      case "joining":
        return "success";
      case "training":
        return "secondary";
      case "experience":
        return "primary";
      case "relieving":
        return "danger";
      default:
        return "primary";
    }
  };

  const sortedKeys = Object.keys(templates).sort((a, b) => {
    const idxA = PREDEFINED_ORDER.indexOf(a);
    const idxB = PREDEFINED_ORDER.indexOf(b);
    if (idxA !== -1 && idxB !== -1) return idxA - idxB;
    if (idxA !== -1) return -1;
    if (idxB !== -1) return 1;
    return a.localeCompare(b);
  });

  return (
    <div className="animate-fade-in" style={{ paddingBottom: 24 }}>
      {/* Top Header */}
      <div
        style={{
          display: "flex",
          alignItems: "center",
          justifyContent: "space-between",
          marginBottom: 24,
          flexWrap: "wrap",
          gap: 12,
        }}
      >
        <div>
          <h2
            style={{
              fontSize: "1.125rem",
              fontWeight: 800,
              margin: 0,
              color: "var(--text-primary)",
              display: "flex",
              alignItems: "center",
              gap: 8,
            }}
          >
            <FileText size={18} style={{ color: "var(--brand-indigo)" }} />
            Staff Document Letters
          </h2>
          <p style={{ margin: 0, fontSize: "0.8125rem", color: "var(--text-muted)", marginTop: 2 }}>
            Download pre-configured letters for {staffData?.name || "Staff"}
          </p>
        </div>
        <BackButton onClick={() => navigate(`/staffProfile/${staffId}`)} />
      </div>

      <div className="row">
        {sortedKeys.map((key) => {
          const tabConfig = PREDEFINED_TABS[key] || {
            label: `${key.charAt(0).toUpperCase() + key.slice(1)} Letter`,
            icon: FileText,
            color: "#8b5cf6",
            bg: "rgba(139, 92, 246, 0.03)",
          };
          const CardIcon = tabConfig.icon;
          const isDownloading = downloadingTemplateKey === key;

          return (
            <div className="col-12 col-md-6 col-xxl-4 mb-4" key={key}>
              <div
                className="card project-box"
                style={{
                  background: "var(--glass-bg)",
                  border: "1px solid var(--glass-border)",
                  borderRadius: 16,
                  height: "100%",
                  display: "flex",
                  flexDirection: "column",
                }}
              >
                <div
                  style={{
                    padding: "16px 20px",
                    borderBottom: "1px solid var(--glass-border)",
                    background: tabConfig.bg,
                    borderTopLeftRadius: 16,
                    borderTopRightRadius: 16,
                  }}
                >
                  <h3
                    style={{
                      margin: 0,
                      fontSize: "0.9375rem",
                      fontWeight: 700,
                      color: tabConfig.color,
                      display: "flex",
                      alignItems: "center",
                      gap: 8,
                    }}
                  >
                    <CardIcon size={16} />
                    {tabConfig.label}
                  </h3>
                </div>

                <div
                  style={{
                    padding: 20,
                    flex: 1,
                    display: "flex",
                    flexDirection: "column",
                    gap: 16,
                  }}
                >
                  <p
                    style={{
                      fontSize: "0.8125rem",
                      color: "var(--text-muted)",
                      margin: 0,
                      lineHeight: 1.6,
                    }}
                  >
                    {getTemplateDescription(key, staffData?.name)}
                  </p>

                  <div
                    style={{
                      borderTop: "1px solid var(--glass-border)",
                      paddingTop: 16,
                      display: "flex",
                      flexWrap: "wrap",
                      gap: 8,
                      marginTop: "auto",
                    }}
                  >
                    <BaseButton
                      color="light"
                      onClick={() => setActivePreviewTemplateKey(key)}
                      disabled={!staffData}
                      style={{
                        display: "inline-flex",
                        alignItems: "center",
                        justifyContent: "center",
                        gap: 6,
                        border: "1px solid var(--glass-border)",
                        background: "var(--icon-btn-bg)",
                        color: "var(--text-primary)",
                        flex: "1 1 110px",
                        whiteSpace: "nowrap",
                        fontSize: "0.8rem",
                      }}
                    >
                      <FileText size={14} />
                      Preview
                    </BaseButton>
                    <BaseButton
                      color={getButtonColor(key)}
                      onClick={() => handleDownload(key)}
                      loader={isDownloading}
                      disabled={isDownloading || !staffData}
                      style={{
                        display: "inline-flex",
                        alignItems: "center",
                        justifyContent: "center",
                        gap: 6,
                        flex: "1 1 140px",
                        whiteSpace: "nowrap",
                        fontSize: "0.8rem",
                      }}
                    >
                      <Download size={14} />
                      Download PDF
                    </BaseButton>
                  </div>
                </div>
              </div>
            </div>
          );
        })}
      </div>

      {/* Consolidated Letter Preview Modal */}
      <BaseModal
        isOpen={activePreviewTemplateKey !== null}
        title={`${activePreviewTemplateKey ? activePreviewTemplateKey.charAt(0).toUpperCase() + activePreviewTemplateKey.slice(1) : ""} Letter Document Preview`}
        toggler={() => setActivePreviewTemplateKey(null)}
        submit={() => {
          if (activePreviewTemplateKey) {
            handleDownload(activePreviewTemplateKey);
            setActivePreviewTemplateKey(null);
          }
        }}
        submitText="Download PDF"
        disabled={downloadingTemplateKey !== null}
        loader={downloadingTemplateKey !== null}
      >
        <div style={{ maxHeight: "70vh", overflowY: "auto", padding: "10px" }}>
          <div style={{ display: "flex", justifyContent: "flex-end", gap: 10, marginBottom: 12 }}>
            <BaseButton
              color="light"
              onClick={() => activePreviewTemplateKey && handlePrint(activePreviewTemplateKey)}
              style={{
                display: "inline-flex",
                alignItems: "center",
                gap: 6,
                border: "1px solid var(--glass-border)",
                background: "var(--icon-btn-bg)",
                color: "var(--text-primary)",
                padding: "6px 12px",
                fontSize: "0.75rem",
              }}
            >
              <Printer size={13} /> Print Letter
            </BaseButton>
          </div>
          {activePreviewTemplateKey && (
            <LetterPreview
              staff={staffData}
              bodyText={getActiveBody(activePreviewTemplateKey)}
              includeSignature={includeSignature}
              signatory={signatoryName}
              signaturePath={signaturePath}
              signatoryTitle={signatoryTitle}
              includeLogo={includeLogo}
              logoPath={logoPath}
              logoWidth={logoWidth}
              logoAlign={logoAlign}
              logoBase64={logoBase64}
              signatureBase64={signatureBase64}
            />
          )}
        </div>
      </BaseModal>
    </div>
  );
};

export default DocumentLetters;
